import * as React from "react";
import CustomModal from "common/components/CustomModal/CustomModal";
import { ModalPanelTransparent } from "../CustomModal/Modal/ModalFrame";
import ModalHelper from "common/helpers/modalHelper";
import "./BountyPickModal.scss";

/**
 * Pick dialog for a bounty game. When the game has no opponent yet, it opens
 * with the opponent step (the anchored team "picks" from the higher seeds),
 * then flows into the winner/score step.
 *
 * @param {object} props
 * @param {any} props.anchorTeam
 * @param {any[]} props.opponents eligible opponent teams (opponent step + change action)
 * @param {any} props.topTeam
 * @param {any} props.bottomTeam null when the opponent still needs picking
 * @param {number} props.bestOf
 * @param {boolean} props.useSeriesScore
 * @param {boolean} props.selectedTop which side was clicked (score step)
 * @param {(teamId: number) => void} props.onPickOpponent
 * @param {(winnerId: number, winnerScore: number, loserScore: number) => void} props.onPickWinner
 */
export default function BountyPickModal({
    anchorTeam,
    opponents,
    topTeam,
    bottomTeam: initialBottomTeam,
    bestOf,
    useSeriesScore,
    selectedTop,
    onPickOpponent,
    onPickWinner,
}) {
    const [bottomTeam, setBottomTeam] = React.useState(initialBottomTeam);
    const [winnerIsTop, setWinnerIsTop] = React.useState(selectedTop ?? true);

    const close = () => {
        ModalHelper.closeAllModals();
    };

    const pickOpponent = (team) => {
        onPickOpponent(team.id);
        setBottomTeam(team);
        setWinnerIsTop(true);
    };

    const applyWinner = (winnerTeam, winnerScore, loserScore) => {
        onPickWinner(winnerTeam.id, winnerScore, loserScore);
        close();
    };

    if (!bottomTeam) {
        return (
            <CustomModal>
                <ModalPanelTransparent>
                    <div className="bounty-pick-modal">
                        <div className="prediction-label">
                            <label>Who does {anchorTeam?.name} pick?</label>
                        </div>
                        <div className="opponent-options">
                            {opponents.map((team) => (
                                <div
                                    key={team.id}
                                    className="opponent-option"
                                    onClick={() => pickOpponent(team)}
                                >
                                    <img src={team.logoUrl} alt="Team Logo" />
                                    <label>{team.name}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </ModalPanelTransparent>
            </CustomModal>
        );
    }

    const winnerTeam = winnerIsTop ? topTeam : bottomTeam;
    const needScore = useSeriesScore && bestOf > 1;
    const winScore = Math.ceil(bestOf / 2);
    const canChangeOpponent = opponents.some((t) => t.id !== bottomTeam.id);

    return (
        <CustomModal>
            <ModalPanelTransparent>
                <div className="bounty-pick-modal">
                    <div className="teams">
                        <div
                            className={`bounty-team${
                                winnerIsTop ? " selected" : ""
                            }`}
                            onClick={() =>
                                needScore
                                    ? setWinnerIsTop(true)
                                    : applyWinner(topTeam, 1, 0)
                            }
                        >
                            <img src={topTeam?.logoUrl} alt="Team Logo" />
                            <label>{topTeam?.name}</label>
                        </div>
                        <div className="win-vs">
                            <div>
                                <label>{needScore ? "Win Vs" : "Or"}</label>
                            </div>
                        </div>
                        <div
                            className={`bounty-team${
                                !winnerIsTop ? " selected" : ""
                            }`}
                            onClick={() =>
                                needScore
                                    ? setWinnerIsTop(false)
                                    : applyWinner(bottomTeam, 1, 0)
                            }
                        >
                            <img src={bottomTeam?.logoUrl} alt="Team Logo" />
                            <label>{bottomTeam?.name}</label>
                        </div>
                    </div>
                    {needScore && (
                        <>
                            <div className="prediction-label">
                                <label>
                                    Predict Best Of {bestOf} Series Score:
                                </label>
                            </div>
                            <div className="score-selection">
                                <div className="predictions">
                                    {Array.from(
                                        { length: winScore },
                                        (v, loserScore) => (
                                            <div
                                                key={loserScore}
                                                className={
                                                    loserScore === 0
                                                        ? "easy-win"
                                                        : loserScore ===
                                                          winScore - 1
                                                        ? "tough-win"
                                                        : "normal-win"
                                                }
                                                onClick={() =>
                                                    applyWinner(
                                                        winnerTeam,
                                                        winScore,
                                                        loserScore
                                                    )
                                                }
                                            >
                                                <img
                                                    src={winnerTeam?.logoUrl}
                                                    alt="Team Logo"
                                                />
                                                <label className="team-name">
                                                    {winnerTeam?.name} win
                                                </label>
                                                <h3>
                                                    {winScore}-{loserScore}
                                                </h3>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                    {canChangeOpponent && (
                        <div className="change-opponent">
                            <label onClick={() => setBottomTeam(null)}>
                                Change opponent
                            </label>
                        </div>
                    )}
                </div>
            </ModalPanelTransparent>
        </CustomModal>
    );
}
